import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatEUR, formatDate, QUOTE_STATUS_PT, PROPOSAL_STATUS_PT, ORDER_STATUS_PT, PAYMENT_STATUS_PT } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowRight, ArrowLeft, Package, FileText, ClipboardList, Layers, Upload } from "lucide-react";
import { toast } from "sonner";

export function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ quotes: [], proposals: [], orders: [], projects: [] });
  useEffect(() => {
    Promise.all([
      api.get("/quotes/mine").catch(() => ({ data: [] })),
      api.get("/proposals/mine").catch(() => ({ data: [] })),
      api.get("/orders/mine").catch(() => ({ data: [] })),
      api.get("/projects/mine").catch(() => ({ data: [] })),
    ]).then(([q, p, o, pr]) => setData({ quotes: q.data, proposals: p.data, orders: o.data, projects: pr.data }));
  }, []);

  const cards = [
    { icon: FileText, label: "Orçamentos", value: data.quotes.length, to: "/portal/orcamentos" },
    { icon: ClipboardList, label: "Propostas", value: data.proposals.length, to: "/portal/propostas" },
    { icon: Layers, label: "Projetos", value: data.projects.length, to: "/portal/projetos" },
    { icon: Package, label: "Encomendas", value: data.orders.length, to: "/portal/encomendas" },
  ];

  return (
    <div className="anim-in">
      <p className="tech-label mb-2">Olá, {user?.name}</p>
      <h1 className="text-3xl font-bold tracking-tighter">Portal cadditive</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="border border-border p-6 bg-card hover:border-foreground group">
            <div className="flex items-start justify-between">
              <c.icon size={22} className="text-brand" />
              <span className="mono text-[10px] uppercase text-muted-foreground">Ver</span>
            </div>
            <div className="text-4xl font-bold mt-6">{c.value}</div>
            <div className="tech-label mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <Section title="Últimos orçamentos" to="/portal/orcamentos" items={data.quotes.slice(0, 4)} render={(q) => (
          <Link key={q.quote_id} to={`/portal/orcamentos/${q.quote_id}`} className="flex items-center justify-between border-b border-border py-3 text-sm hover:bg-secondary px-2">
            <div className="min-w-0"><div className="mono text-xs">{q.code}</div><div className="font-medium truncate">{q.title}</div></div>
            <StatusBadge status={q.status} label={QUOTE_STATUS_PT[q.status]} />
          </Link>
        )} />
        <Section title="Últimas encomendas" to="/portal/encomendas" items={data.orders.slice(0, 4)} render={(o) => (
          <Link key={o.order_id} to={`/portal/encomendas/${o.order_id}`} className="flex items-center justify-between border-b border-border py-3 text-sm hover:bg-secondary px-2">
            <div className="min-w-0"><div className="mono text-xs">{o.code}</div><div className="font-medium">{formatEUR(o.total)} · {formatDate(o.created_at)}</div></div>
            <StatusBadge status={o.status} label={ORDER_STATUS_PT[o.status]} />
          </Link>
        )} />
      </div>
    </div>
  );
}

const Section = ({ title, to, items, render }) => (
  <div className="border border-border p-5 bg-card">
    <div className="flex items-center justify-between mb-3">
      <p className="tech-label">{title}</p>
      <Link to={to} className="text-xs link-underline">Ver todos</Link>
    </div>
    {items.length ? items.map(render) : <p className="tech-label py-6 text-center">sem registos</p>}
  </div>
);

export function ClientQuotes() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/quotes/mine").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Os meus orçamentos</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Referência</Th><Th>Título</Th><Th>Estado</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {items.map((q) => (
              <tr key={q.quote_id} className="border-t border-border hover:bg-secondary">
                <Td className="mono">{q.code}</Td>
                <Td>{q.title}</Td>
                <Td><StatusBadge status={q.status} label={QUOTE_STATUS_PT[q.status]} /></Td>
                <Td>{formatDate(q.created_at)}</Td>
                <Td><Link to={`/portal/orcamentos/${q.quote_id}`} className="link-underline text-brand">Abrir</Link></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center tech-label">sem pedidos</td></tr>}
          </tbody>
        </table>
      </div>
      <Link to="/pedido-orcamento" className="btn-primary mt-6">Novo pedido <ArrowRight size={14} /></Link>
    </div>
  );
}

export function ClientQuoteDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data));
    api.get(`/messages/quote:${id}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [id]);
  async function send() {
    if (!body.trim()) return;
    try {
      const { data } = await api.post("/messages", { thread_key: `quote:${id}`, body });
      setMessages((m) => [...m, data]); setBody("");
    } catch (e) { toast.error(apiError(e)); }
  }
  if (!q) return <div className="tech-label">a carregar…</div>;
  return (
    <div className="anim-in">
      <Link to="/portal/orcamentos" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <p className="tech-label">{q.code}</p>
      <h1 className="text-3xl font-bold tracking-tighter">{q.title}</h1>
      <div className="mt-3"><StatusBadge status={q.status} label={QUOTE_STATUS_PT[q.status]} /></div>
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Descrição"><p className="text-sm whitespace-pre-line">{q.description}</p></Card>
          <div className="grid sm:grid-cols-2 gap-4">
            <Card title="Detalhes">
              <Row k="Quantidade" v={q.quantity} />
              <Row k="Dimensões" v={q.dimensions || "—"} />
              <Row k="Prazo" v={q.deadline ? formatDate(q.deadline) : "—"} />
              <Row k="Urgência" v={q.urgency} />
            </Card>
            <Card title="Preferências">
              <Row k="Material" v={q.material_pref || "a recomendar"} />
              <Row k="Cor" v={q.color_pref || "—"} />
              {Object.entries(q.requirements || {}).filter(([_, v]) => v).map(([k]) => <Row key={k} k={k} v="✓" />)}
            </Card>
          </div>
        </div>
        <div className="space-y-4">
          <Card title="Mensagens">
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {messages.map((m) => (
                <div key={m.message_id} className={`p-3 border ${m.author_role === "customer" ? "border-border" : "border-brand bg-brand/5"}`}>
                  <p className="mono text-[10px] uppercase text-muted-foreground">{m.author_name} · {formatDate(m.created_at)}</p>
                  <p className="text-sm mt-1">{m.body}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="tech-label text-center py-4">sem mensagens</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <input className="input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Escrever…" data-testid="msg-input" />
              <button onClick={send} className="btn-primary text-xs" data-testid="msg-send">Enviar</button>
            </div>
          </Card>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:var(--brand)}`}</style>
    </div>
  );
}

export function ClientProposals() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/proposals/mine").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Propostas</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Referência</Th><Th>Total</Th><Th>Estado</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.proposal_id} className="border-t border-border">
                <Td className="mono">{p.code}</Td>
                <Td className="font-medium">{formatEUR(p.total)}</Td>
                <Td><StatusBadge status={p.status} label={PROPOSAL_STATUS_PT[p.status]} /></Td>
                <Td>{formatDate(p.created_at)}</Td>
                <Td><Link className="link-underline text-brand" to={`/portal/propostas/${p.proposal_id}`}>Abrir</Link></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center tech-label">sem propostas</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClientProposalDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const nav = useNavigate();
  const reload = () => api.get(`/proposals/${id}`).then((r) => setP(r.data));
  useEffect(() => { reload(); }, [id]);

  async function decide(decision) {
    try {
      await api.post(`/proposals/${id}/decision`, { decision });
      toast.success("Decisão enviada");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  }

  if (!p) return <div className="tech-label">a carregar…</div>;
  return (
    <div className="anim-in max-w-4xl">
      <Link to="/portal/propostas" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <div className="border border-border p-8 bg-card crosshair-corners">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="tech-label">Proposta</p>
            <h1 className="text-3xl font-bold tracking-tighter">{p.code}</h1>
          </div>
          <StatusBadge status={p.status} label={PROPOSAL_STATUS_PT[p.status]} />
        </div>
        <table className="w-full mt-8 text-sm">
          <thead><tr className="text-left border-b border-border"><Th>Descrição</Th><Th>Qtd</Th><Th className="text-right">Unit.</Th><Th className="text-right">Total</Th></tr></thead>
          <tbody>
            {p.lines.map((l, i) => (
              <tr key={i} className="border-b border-border"><Td>{l.description}</Td><Td className="mono">{l.quantity}</Td><Td className="text-right font-medium">{formatEUR(l.unit_price)}</Td><Td className="text-right font-medium">{formatEUR(l.total)}</Td></tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 space-y-1 text-sm max-w-sm ml-auto">
          <Row k="Subtotal" v={formatEUR(p.subtotal)} />
          <Row k={`IVA ${p.vat_rate}%`} v={formatEUR(p.vat_amount)} />
          <Row k="Portes" v={formatEUR(p.shipping)} />
          <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="font-bold">Total</span><span className="font-bold text-lg">{formatEUR(p.total)}</span></div>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          <Info k="Prazo estimado" v={`${p.lead_time_days} dias`} />
          <Info k="Pagamento" v={p.payment_terms} />
          <Info k="Validade" v={`${p.validity_days} dias`} />
        </div>
        {p.observations && <div className="mt-6 border-t border-border pt-4"><p className="tech-label mb-2">Observações</p><p className="text-sm">{p.observations}</p></div>}
        {p.status === "sent" && (
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => decide("accept")} className="btn-primary" data-testid="proposal-accept">Aceitar proposta</button>
            <button onClick={() => decide("request_changes")} className="btn-outline" data-testid="proposal-changes">Pedir alterações</button>
            <button onClick={() => decide("reject")} className="btn-outline text-destructive" data-testid="proposal-reject">Rejeitar</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ClientProjects() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/projects/mine").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Projetos</h1>
      <div className="grid gap-3">
        {items.map((p) => (
          <div key={p.project_id} className="border border-border p-4 bg-card">
            <div className="flex justify-between"><span className="mono text-xs">{p.code}</span><StatusBadge status={p.status} label={p.status} /></div>
            <h3 className="font-semibold mt-1">{p.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-10 tech-label">sem projetos</div>}
      </div>
    </div>
  );
}

export function ClientOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/orders/mine").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Encomendas</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Referência</Th><Th>Total</Th><Th>Estado</Th><Th>Pagamento</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.order_id} className="border-t border-border">
                <Td className="mono">{o.code}</Td>
                <Td className="font-medium">{formatEUR(o.total)}</Td>
                <Td><StatusBadge status={o.status} label={ORDER_STATUS_PT[o.status]} /></Td>
                <Td><StatusBadge status={o.payment_status} label={PAYMENT_STATUS_PT[o.payment_status]} /></Td>
                <Td>{formatDate(o.created_at)}</Td>
                <Td><Link className="text-brand link-underline" to={`/portal/encomendas/${o.order_id}`}>Abrir</Link></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center p-8 tech-label">sem encomendas</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClientOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const reload = () => api.get(`/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { reload(); }, [id]);

  async function reportPayment(method) {
    let file_id = null;
    if (proofFile) {
      const fd = new FormData(); fd.append("file", proofFile);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      file_id = data.file_id;
    }
    try {
      await api.post(`/orders/${id}/payment-proof`, { file_id, method, note: `Pagamento via ${method}` });
      toast.success("Comprovativo comunicado");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  }

  if (!o) return <div className="tech-label">a carregar…</div>;
  return (
    <div className="anim-in">
      <Link to="/portal/encomendas" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <p className="tech-label">{o.code}</p>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <h1 className="text-3xl font-bold tracking-tighter">Encomenda</h1>
        <StatusBadge status={o.status} label={ORDER_STATUS_PT[o.status]} />
        <StatusBadge status={o.payment_status} label={PAYMENT_STATUS_PT[o.payment_status]} />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Linhas">
            {o.lines.map((l) => (
              <div key={l.line_id} className="flex justify-between py-3 border-b border-border last:border-b-0">
                <div>
                  <div className="font-medium">{l.name} × {l.quantity}</div>
                  <div className="mono text-[10px] text-muted-foreground uppercase">{Object.entries(l.variant || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                  <StatusBadge status={l.status} label={l.status} />
                </div>
                <div className="text-right font-medium">{formatEUR(l.unit_price * l.quantity)}</div>
              </div>
            ))}
          </Card>
          <Card title="Envio">
            <p className="text-sm">{o.shipping_address.name}</p>
            <p className="text-sm text-muted-foreground">{o.shipping_address.line1}, {o.shipping_address.postal_code} {o.shipping_address.city}</p>
            {o.shipment && (
              <div className="mt-3 border-t border-border pt-3">
                <p className="tech-label">Rastreamento</p>
                <p className="mono text-sm mt-1">{o.shipment.tracking_code}</p>
                <p className="text-xs text-muted-foreground">{o.shipment.carrier} · Enviado em {formatDate(o.shipment.shipped_at)}</p>
              </div>
            )}
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Resumo">
            <Row k="Subtotal" v={formatEUR(o.subtotal)} />
            <Row k={`IVA ${o.vat_rate}%`} v={formatEUR(o.vat_amount)} />
            <Row k="Portes" v={formatEUR(o.shipping_cost)} />
            <div className="flex justify-between border-t border-border pt-2 mt-2 font-bold"><span>Total</span><span>{formatEUR(o.total)}</span></div>
          </Card>
          {o.payment_status === "waiting" && (
            <Card title="Pagamento">
              {o.payment_method === "mbway" && <p className="text-sm">Pague <b>{formatEUR(o.total)}</b> via MB WAY para <b className="mono">+351 912 000 000</b> (titular: cadditive). Indique {o.code} nas observações.</p>}
              {o.payment_method === "bank_transfer" && <p className="text-sm">Transferência para IBAN <b className="mono">PT50 0000 0000 0000 0000 0000 0</b> · titular cadditive Lda. Referência: <b>{o.code}</b></p>}
              {o.payment_method === "after_quote" && <p className="text-sm">Aguardaremos confirmação por email para prosseguir.</p>}
              <div className="mt-4">
                <label className="tech-label block mb-2">Anexar comprovativo (opcional)</label>
                <input type="file" onChange={(e) => setProofFile(e.target.files?.[0])} className="text-xs" data-testid="payment-proof-file" />
                <button onClick={() => reportPayment(o.payment_method)} className="btn-primary w-full justify-center mt-3 text-sm" data-testid="payment-report">Já efetuei o pagamento</button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export function ClientFiles() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/files/mine").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const download = async (id, name) => {
    try {
      const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao descarregar"); }
  };
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Ficheiros</h1>
      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Nome</Th><Th>Tipo</Th><Th>Tamanho</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.file_id} className="border-t border-border">
                <Td>{f.original_filename}</Td>
                <Td className="mono text-xs">{f.content_type}</Td>
                <Td className="mono text-xs">{(f.size / 1024).toFixed(0)} KB</Td>
                <Td>{formatDate(f.created_at)}</Td>
                <Td><button onClick={() => download(f.file_id, f.original_filename)} className="text-brand link-underline">Descarregar</button></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center tech-label">sem ficheiros</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 tech-label ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Row = ({ k, v }) => (<div className="flex justify-between text-sm py-1"><span className="tech-label">{k}</span><span className="font-medium text-right">{v}</span></div>);
const Info = ({ k, v }) => (<div><p className="tech-label">{k}</p><p className="font-medium mt-1">{v}</p></div>);
const Card = ({ title, children }) => (
  <div className="border border-border p-5 bg-card">
    <p className="tech-label mb-3">{title}</p>
    {children}
  </div>
);
